// Horizontal scroll-arrow controls for the stage-card slider — same
// pattern as tayab-terroir-map.js's scrollVarieties()/updateArrowState(),
// stripped of that component's matchMedia vertical/horizontal branch:
// this slider only ever scrolls left/right, at every breakpoint, so
// there's a single code path instead of one per axis. Also drives the
// 01—02—03 connector: clicking a number scrolls to that stage's card,
// and scrolling/swiping the row (by drag or by the arrow buttons)
// keeps whichever number is highlighted in sync with what's actually
// in view.
class TayabLabTesting extends HTMLElement {
  connectedCallback() {
    this.gridEl = this.querySelector('.tayab-lab-testing__grid');
    this.prevArrowEl = this.querySelector('.tayab-lab-testing__arrow--prev');
    this.nextArrowEl = this.querySelector('.tayab-lab-testing__arrow--next');
    this.cardEls = Array.from(this.querySelectorAll('.tayab-lab-testing__card'));
    this.connectorEls = Array.from(this.querySelectorAll('.tayab-lab-testing__connector-circle'));

    if (!this.gridEl || !this.prevArrowEl || !this.nextArrowEl) return;

    // Tracks which card a connector click asked to see, so
    // updateActiveCard() can honor that explicit choice instead of
    // re-deriving it from scroll position alone — see the comment on
    // scrollToCard() for why the geometric rule by itself isn't enough.
    this.pendingActiveIndex = null;

    this.prevArrowEl.addEventListener('click', () => this.scrollCards(-1));
    this.nextArrowEl.addEventListener('click', () => this.scrollCards(1));

    this.connectorEls.forEach((btn, index) => {
      btn.addEventListener('click', () => this.scrollToCard(index));
    });

    // A direct gesture on the row itself means the user has taken
    // navigation into their own hands (drag/swipe/wheel) — clear
    // whichever connector click we were still honoring so the
    // geometric rule in updateActiveCard() takes back over and starts
    // live-tracking the manual scroll, instead of the old click target
    // staying artificially stuck.
    ['pointerdown', 'wheel', 'touchstart'].forEach((eventName) => {
      this.gridEl.addEventListener(eventName, () => {
        this.pendingActiveIndex = null;
      }, { passive: true });
    });

    // Single rAF-throttled scroll handler drives both the arrow disabled
    // state and the connector's active number — native scroll events can
    // fire many times per frame during a drag/swipe, and both updates
    // need the same live scrollLeft/layout reads, so one scheduled
    // callback covers both instead of two competing listeners.
    let scrollScheduled = false;
    this.gridEl.addEventListener('scroll', () => {
      if (scrollScheduled) return;
      scrollScheduled = true;
      requestAnimationFrame(() => {
        scrollScheduled = false;
        this.updateArrowState();
        this.updateActiveCard();
      });
    }, { passive: true });

    let resizeScheduled = false;
    window.addEventListener('resize', () => {
      if (resizeScheduled) return;
      resizeScheduled = true;
      requestAnimationFrame(() => {
        resizeScheduled = false;
        this.updateArrowState();
        this.updateActiveCard();
      });
    });

    this.updateArrowState();
    this.updateActiveCard();
  }

  // Scrolls by one card's width plus the row's gap, measured live from
  // the first rendered card rather than a fixed guess — matches
  // whichever of the three responsive width tiers is currently active.
  // Clears pendingActiveIndex so a stepped arrow scroll always falls
  // through to updateActiveCard()'s geometric rule, same as any other
  // organic scroll, rather than keeping an earlier connector click
  // artificially pinned.
  scrollCards(direction) {
    this.pendingActiveIndex = null;
    const firstCard = this.gridEl.querySelector('.tayab-lab-testing__card');
    const gapPx = parseFloat(getComputedStyle(this.gridEl).columnGap) || 0;
    const stepPx = firstCard ? firstCard.getBoundingClientRect().width + gapPx : 300;
    this.gridEl.scrollBy({ left: direction * stepPx, behavior: 'smooth' });
  }

  // Scrolls to the given card. Tablet/mobile scroll it flush to the
  // row's left edge — with ~1-1.7 cards visible there, that's already
  // close to "center it" and matches the flush-left/peek-right
  // convention the arrow buttons and initial layout already use.
  //
  // Desktop instead centers the card in the visible row (offsetLeft
  // minus half the leftover space between card and row width), since
  // at ~2.3 cards visible, left-edge alignment left card 2 and card 3
  // clamping to the exact same scrollLeft (both exceed the max
  // scrollable position) — centering gives each card its own distinct
  // target. Cards 1 and 3 still can't fully center without
  // overscrolling past either end, so the result clamps to [0,
  // maxScrollLeft]: card 1 clamps to 0 (flush-left, same as the
  // initial/default view), card 3 clamps to maxScrollLeft (scrolled as
  // far right as the layout allows) — only card 2, the one with room
  // on both sides, reaches its true centered position unclamped.
  //
  // pendingActiveIndex records that this click is what's driving the
  // scroll, and updateActiveCard() honors it as-is (no geometric
  // recompute) until something supersedes it — necessary defense
  // whenever two cards' targets could still coincide (e.g. cards 1 and
  // 3 both clamping to an end), since geometry alone can't tell two
  // different clicks apart once they land on the same scrollLeft.
  scrollToCard(index) {
    const card = this.cardEls[index];
    if (!card) return;
    this.pendingActiveIndex = index;
    this.setActiveIndex(index);

    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    let targetLeft = card.offsetLeft;
    if (isDesktop) {
      const cardWidth = card.getBoundingClientRect().width;
      const maxScrollLeft = this.gridEl.scrollWidth - this.gridEl.clientWidth;
      const centered = card.offsetLeft - (this.gridEl.clientWidth - cardWidth) / 2;
      targetLeft = Math.max(0, Math.min(centered, maxScrollLeft));
    }

    this.gridEl.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }

  // Disables (dims, doesn't hide) whichever arrow has nothing left to
  // scroll toward — same reasoning as Terroir Map: hiding would
  // reflow the row's available width every time you hit an edge.
  updateArrowState() {
    const { scrollLeft, scrollWidth, clientWidth } = this.gridEl;
    this.prevArrowEl.disabled = scrollLeft <= 1;
    this.nextArrowEl.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  }

  // Determines which card counts as "active" right now and syncs the
  // connector's highlighted number to match.
  //
  // A pending connector click always wins outright (see scrollToCard())
  // — it's cleared by scrollCards()/a manual gesture on the row, never
  // recomputed here, so it can't be second-guessed by geometry mid-flight.
  //
  // Otherwise, "active" is defined as whichever card's left edge sits
  // closest to the row's own left edge (offsetLeft - scrollLeft,
  // smallest non-negative value) — the card that has most recently
  // arrived at the front. The peek layout means more than one card can
  // be simultaneously visible, so at the scroll-max boundary that rule
  // alone can pick a card other than the last one: on desktop's
  // ~2.3-card peek, the 2nd and 3rd cards are both simultaneously fully
  // visible once scrolled all the way right, and the 2nd card's offset
  // is smaller, so it would keep "winning" even though there's nowhere
  // further right to scroll. Once scrollLeft has reached its maximum
  // (within a 1px rounding tolerance), the last card is treated as
  // active instead — the intuitive answer for arriving at the end via
  // a manual drag/swipe with no click involved at all.
  updateActiveCard() {
    if (!this.cardEls.length || !this.connectorEls.length) return;

    if (this.pendingActiveIndex !== null) {
      this.setActiveIndex(this.pendingActiveIndex);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = this.gridEl;
    const maxScrollLeft = scrollWidth - clientWidth;

    let activeIndex = 0;
    if (maxScrollLeft <= 1 || scrollLeft >= maxScrollLeft - 1) {
      activeIndex = this.cardEls.length - 1;
    } else {
      let closestOffset = Infinity;
      this.cardEls.forEach((card, index) => {
        const offset = card.offsetLeft - scrollLeft;
        if (offset >= -1 && offset < closestOffset) {
          closestOffset = offset;
          activeIndex = index;
        }
      });
    }

    this.setActiveIndex(activeIndex);
  }

  setActiveIndex(activeIndex) {
    this.connectorEls.forEach((btn, index) => {
      const isActive = index === activeIndex;
      btn.classList.toggle('is-active', isActive);
      if (isActive) {
        btn.setAttribute('aria-current', 'true');
      } else {
        btn.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('tayab-lab-testing')) {
  customElements.define('tayab-lab-testing', TayabLabTesting);
}
